"use client";

import NextLink from "next/link";
import React, { ReactNode, useMemo } from "react";
import { useSWRConfig } from "swr";

import Space from "@/components/common/space";
import { Title } from "@/components/trade/trade.styled";
import { Available } from "@/components/transaction/styled";
import { useWeb3Context } from "@/contexts/web3";
import { PositionType } from "@/contracts/transform/position";
import { usePoolData } from "@/hooks/position";
import { ChainSearchResponse } from "@/types/api";
import { fallbackExtract } from "@/util/api/api";

import { checkIsWatch } from "../../util/position-helpers";
import { Pool } from "./pool";
import { AllTradesTitle, AssetsGrid } from "./styled";

interface Props {
  chainName: string;
}

const WithPosition = ({
  address,
  children,
}: {
  address: `0x${string}`;
  children: (pType?: PositionType) => ReactNode;
}) => {
  const type = usePoolData(address).position?.type;
  return children(type);
};

const AllTrades = (props: Props) => {
  const { fallback } = useSWRConfig();

  const fInternalChainId = useMemo(() => {
    const entries = fallbackExtract(fallback, "chainSearch");

    if (!entries.length) {
      return;
    }

    const [[, { chainInfo }]] = entries as [
      [string, ChainSearchResponse, RegExpExecArray],
    ];
    return chainInfo?.chainId;
  }, [fallback]);

  const {
    pools,
    internalChainId: _internalChainId,
    getPoolByAddress,
  } = useWeb3Context();
  const internalChainId = _internalChainId ?? fInternalChainId;

  const tradePools = pools.filter((pool) => !pool.finished);

  return (
    <>
      <AllTradesTitle>
        <Title>Trade</Title>
        <Space height={8} heightMobile={8} />
        {tradePools.length > 0 ? (
          <Available>With up to 20&times;&nbsp;leverage.</Available>
        ) : (
          <Available>
            Trading pools are coming soon.
            <br />
            Looking for a particular token to leverage trade?&nbsp;
            <NextLink
              href="https://discord.com/invite/tCTYwTzM8h"
              target="_blank"
            >
              Tell us more!
            </NextLink>
          </Available>
        )}
        <Space height={32} heightMobile={32} />
      </AllTradesTitle>

      <AssetsGrid>
        {tradePools?.map(({ address, awaiting }) => (
          <WithPosition address={address} key={address}>
            {(position) => {
              return (
                <NextLink
                  href={
                    awaiting
                      ? `/${props.chainName}/lend/${address}`
                      : checkIsWatch(position)
                      ? `/${props.chainName}/watch-position/${address}`
                      : `/${props.chainName}/trade/${address}`
                  }
                >
                  <div className="pool">
                    <Pool
                      position={position}
                      internalChainId={internalChainId! /* fixme */}
                      address={address}
                    />
                  </div>
                </NextLink>
              );
            }}
          </WithPosition>
        ))}
      </AssetsGrid>
    </>
  );
};

export default AllTrades;
